import { useState, useEffect, useCallback } from "react";
import "../../Components/datagrid-override.css";
import axios from "axios";
import Helpers from "../../Config/Helpers";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  IconButton,
  Box,
  Paper,
  Typography,
  TextField,
  Stack,
  Tabs,
  Tab,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HomeIcon from "@mui/icons-material/Home";
import ReusableFormDialog from "../FormModal/ReusableFormDialog";
import DeleteConfirmationDialog from "../FormModal/DeleteConfirmationDialog";
import ReusableDataGrid from "../../Components/ReusableDataGrid";
import PageShell from "./PageShell";

function ModuleLayout({
  moduleName,
  moduleKey,
  icon,
  initialFormValues,
  formFields,
  validationSchema,
  columns,
  searchFilter,
  sortFunction,
  getItemName,
  deleteWarning,
  onBeforeSave,
  mapItemToFormValues,
  onAfterFetch,
  additionalActions = [],
  quickFilters = [],
  summaryChips = [],
  headerSummary,
  onRowClick,
  customActionsRenderer,
  topSpacing = { xs: 10, sm: 11, md: 12 },
  breadcrumbs,
  moduleDescription,
  footerHeight = 64,
  primaryAction,
  tabs = [],
  activeTab,
  onTabChange,
  fetchEndpoint,
  incrementalPageSize,
}) {
  const current_url = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [btnLoading, setBtnLoading] = useState(false);
  const [openFormDialog, setOpenFormDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [items, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState(initialFormValues);
  const [perms, setPerms] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextPage, setNextPage] = useState(2);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [moduleKey, current_url.pathname]);

  const getSaveEndpoint = (editing) =>
    `${moduleKey}/${editing ? "update" : "add"}`;

  const getDeleteEndpoint = (itemId) => `${moduleKey}/delete/${itemId}`;

  const defaultSearchFilter = (item) => {
    if (!search) return true;
    const term = search.toLowerCase();
    return Object.values(item).some((value) =>
      String(value || "")
        .toLowerCase()
        .includes(term),
    );
  };

  const defaultSortFunction = (a, b) => {
    const aName = Object.values(a)[1] || "";
    const bName = Object.values(b)[1] || "";
    return aName.localeCompare(bName, undefined, { sensitivity: "base" });
  };

  const filteredItems = items
    .filter((item) =>
      searchFilter ? searchFilter(item, search) : defaultSearchFilter(item),
    )
    .sort(sortFunction || defaultSortFunction);

  const getItems = useCallback(() => {
    const defaultEndpoint =
      moduleKey === "teams"
        ? `${moduleKey}/${Helpers.authParentId}`
        : `${moduleKey}/all/${Helpers.authParentId}`;
    const resolvedEndpoint = fetchEndpoint || defaultEndpoint;
    const requestEndpoint = incrementalPageSize
      ? `${resolvedEndpoint}${resolvedEndpoint.includes("?") ? "&" : "?"}page=1&limit=${incrementalPageSize}`
      : resolvedEndpoint;
    setLoading(true);
    axios
      .get(`${Helpers.baseUrl}/${requestEndpoint}`, Helpers.headers)
      .then((response) => {
        const data = Array.isArray(response.data?.items)
          ? response.data.items
          : Array.isArray(response.data)
            ? response.data.reverse()
            : [];
        setItems(data);
        setNextPage(2);
        setHasMore(Boolean(response.data?.hasMore));
        if (onAfterFetch) onAfterFetch(data);
      })
      .catch((error) => Helpers.unauthenticated(error, navigate))
      .finally(() => setLoading(false));
  }, [fetchEndpoint, incrementalPageSize, moduleKey, navigate, onAfterFetch]);

  const loadMoreItems = () => {
    if (!fetchEndpoint || !incrementalPageSize || loadingMore || !hasMore) return;
    const separator = fetchEndpoint.includes("?") ? "&" : "?";
    setLoadingMore(true);
    axios
      .get(
        `${Helpers.baseUrl}/${fetchEndpoint}${separator}page=${nextPage}&limit=${incrementalPageSize}`,
        Helpers.headers,
      )
      .then((response) => {
        const newItems = Array.isArray(response.data?.items)
          ? response.data.items
          : [];
        setItems((current) => [...current, ...newItems]);
        setNextPage((current) => current + 1);
        setHasMore(Boolean(response.data?.hasMore));
      })
      .catch((error) => Helpers.unauthenticated(error, navigate))
      .finally(() => setLoadingMore(false));
  };

  const handleSave = (values) => {
    setBtnLoading(true);
    const finalValues = onBeforeSave ? onBeforeSave(values) : values;
    axios
      .post(
        `${Helpers.baseUrl}/${getSaveEndpoint(isEditing)}`,
        finalValues,
        Helpers.headers,
      )
      .then(() => {
        getItems();
        setCurrentItem(initialFormValues);
        setIsEditing(false);
        setOpenFormDialog(false);
        Helpers.toast(
          "success",
          `${moduleName.slice(0, -1)} saved successfully`,
        );
        setBtnLoading(false);
      })
      .catch((error) => {
        Helpers.toast("error", Helpers.error_response(error));
        setBtnLoading(false);
      });
  };

  const handleEdit = (item) => {
    setIsEditing(true);
    setCurrentItem(mapItemToFormValues ? mapItemToFormValues(item) : item);
    setOpenFormDialog(true);
  };

  const handleAddNew = () => {
    setIsEditing(false);
    setCurrentItem(initialFormValues);
    setOpenFormDialog(true);
  };

  const handleDelete = (item) => {
    setItemToDelete(item);
    setOpenDeleteDialog(true);
  };

  const confirmDelete = () => {
    if (!itemToDelete) return;
    setDeleteLoading(true);
    axios
      .get(
        `${Helpers.baseUrl}/${getDeleteEndpoint(itemToDelete._id)}`,
        Helpers.headers,
      )
      .then(() => {
        getItems();
        setOpenDeleteDialog(false);
        setItemToDelete(null);
        setDeleteLoading(false);
        Helpers.toast(
          "success",
          `${moduleName.slice(0, -1)} deleted successfully`,
        );
      })
      .catch((error) => {
        Helpers.unauthenticated(error, navigate);
        setOpenDeleteDialog(false);
        setItemToDelete(null);
        setDeleteLoading(false);
      });
  };

  const getPerms = useCallback(() => {
    const link = current_url.pathname;
    const permissions = JSON.parse(localStorage.getItem("permissions"));
    if (permissions) {
      const perm = permissions.find((p) => p.tab_link === link);
      setPerms(perm || {});
    }
  }, [current_url.pathname]);

  useEffect(() => {
    getItems();
    getPerms();
  }, [getItems, getPerms]);

  const canCreate =
    Helpers.authUser.user_role == null || perms.can_create === 1;
  const canUpdate =
    Helpers.authUser.user_role == null || perms.can_update === 1;
  const canDelete =
    Helpers.authUser.user_role == null || perms.can_delete === 1;

  const defaultActionsRenderer = (params) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "100%",
        gap: 1,
      }}
    >
      {canUpdate && (
        <IconButton
          onClick={() => handleEdit(params.row)}
          color="success"
          size="small"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      )}
      {canDelete && (
        <IconButton
          onClick={() => handleDelete(params.row)}
          color="error"
          size="small"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  const wrappedCustomActionsRenderer = customActionsRenderer
    ? (params) =>
        customActionsRenderer(
          params,
          canUpdate,
          canDelete,
          handleEdit,
          handleDelete,
        )
    : null;

  const enhancedColumns = columns.some((col) => col.field === "actions")
    ? columns
    : [
        ...columns,
        {
          field: "actions",
          headerName: "Actions",
          width: 120,
          sortable: false,
          filterable: false,
          renderCell: wrappedCustomActionsRenderer || defaultActionsRenderer,
        },
      ];

  const rows = filteredItems.map((item, index) => ({
    ...item,
    id: item._id,
    serial: index + 1,
  }));

  const allChips = [
    ...summaryChips,
    ...(filteredItems.length > 0
      ? [
          {
            label: `${filteredItems.length} ${
              filteredItems.length === 1 ? "item" : "items"
            }`,
          },
        ]
      : []),
  ];

  const defaultBreadcrumbs = [
    {
      label: "Home",
      path: "/user/dashboard",
      icon: <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />,
    },
    { label: moduleName, path: null },
  ];

  const primaryBtnLabel =
    primaryAction?.label ?? `Add ${moduleName.slice(0, -1)}`;
  const primaryBtnClick = primaryAction?.onClick ?? handleAddNew;
  const primaryBtnTo = primaryAction?.to;
  const resolvedFormFields =
    typeof formFields === "function" ? formFields(currentItem) : formFields;
  const hasTabs = tabs.length > 0;
  const handleTabChange = (_, nextValue) => {
    setSearch("");
    if (onTabChange) onTabChange(nextValue);
  };
  const headerActions = canCreate ? (
    <Stack direction="row" spacing={1} flexWrap="wrap">
      <Button
        variant="contained"
        startIcon={<AddIcon fontSize="small" />}
        component={primaryBtnTo ? Link : "button"}
        to={primaryBtnTo}
        onClick={primaryBtnTo ? undefined : primaryBtnClick}
        sx={{
          fontSize: 12,
          fontWeight: 400,
          textTransform: "none",
          whiteSpace: "nowrap",
          backgroundColor: "#36589A",
          borderRadius: 6,
          padding: "4px 8px",
          fontFamily: 'Inter, "Segoe UI", sans-serif',
          "&:hover": {
            backgroundColor: "#2E4D88",
            color: "#FFFFFF !important",
          },
        }}
      >
        {primaryBtnLabel}
      </Button>
      {additionalActions.map((action, index) => (
        <Button
          key={index}
          variant="contained"
          onClick={action.onClick}
          sx={{
            backgroundColor: "rgba(255,255,255,0.2)",
            backdropFilter: "blur(10px)",
            color: "white",
            border: "1px solid rgba(255,255,255,0.3)",
            textTransform: "none",
            whiteSpace: "nowrap",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.3)" },
          }}
        >
          {action.label}
        </Button>
      ))}
    </Stack>
  ) : null;

  return (
    <>
      <PageShell
        title={moduleName}
        description={moduleDescription}
        icon={icon}
        breadcrumbs={breadcrumbs || defaultBreadcrumbs}
        summaryChips={allChips}
        actions={headerActions}
        topSpacing={topSpacing}
        footerHeight={footerHeight}
        variant="list"
      >
        {/* ── Data Table Card ── */}
        <Paper
          elevation={1}
          sx={{
            borderRadius: 2,
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            minHeight: 0,
            mb: 2,
          }}
        >
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              background: "linear-gradient(to right, #f8f9fa 0%, #ffffff 100%)",
              flexShrink: 0,
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
              justifyContent="space-between"
            >
              {hasTabs ? (
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  sx={{
                    minHeight: 40,
                    "& .MuiTab-root": {
                      minHeight: 40,
                      textTransform: "none",
                      fontWeight: 600,
                    },
                  }}
                >
                  {tabs.map((tab) => (
                    <Tab
                      key={tab.value}
                      value={tab.value}
                      icon={tab.icon}
                      iconPosition={tab.iconPosition || "start"}
                      label={tab.label}
                    />
                  ))}
                </Tabs>
              ) : (
                <Typography
                  variant="h6"
                  component="h3"
                  sx={{ fontWeight: 600, color: "text.primary" }}
                >
                  All {moduleName}
                </Typography>
              )}

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1}
                sx={{ minWidth: { sm: "auto", md: "50%" } }}
              >
                <TextField
                  label="Search"
                  variant="outlined"
                  size="small"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{ flexGrow: 1, minWidth: { xs: "100%", sm: "200px" } }}
                />
              </Stack>
            </Stack>

            {(quickFilters.length > 0 || headerSummary) && (
              <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                {quickFilters.length > 0 && (
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {quickFilters.map((filter) => (
                      <Button
                        key={filter.key}
                        variant={filter.active ? "contained" : "outlined"}
                        size="small"
                        onClick={filter.onClick}
                        sx={{
                          textTransform: "none",
                          whiteSpace: "nowrap",
                          minWidth: 72,
                        }}
                      >
                        {filter.label}
                      </Button>
                    ))}
                  </Box>
                )}
                {headerSummary && <Box sx={{ ml: "auto" }}>{headerSummary}</Box>}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              flex: 1,
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              transition: "width 0.25s ease",
              willChange: "width",
            }}
          >
            <ReusableDataGrid
              rows={rows}
              loading={loading}
              columns={enhancedColumns}
              onRowClick={onRowClick}
              pageSize={10}
              pageSizeOptions={[10, 25, 50, 100]}
              noRowsMessage={`No ${moduleName.toLowerCase()} available to display`}
              autoHeight={false}
              sx={{
                flex: 1,
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: "rgba(102, 126, 234, 0.04)",
                },
              }}
            />
            {incrementalPageSize && hasMore && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  p: 1.5,
                  borderTop: 1,
                  borderColor: "divider",
                  flexShrink: 0,
                }}
              >
                <Button
                  variant="outlined"
                  onClick={loadMoreItems}
                  disabled={loadingMore}
                  sx={{ textTransform: "none", minWidth: 160 }}
                >
                  {loadingMore ? "Loading..." : `Load ${incrementalPageSize} more`}
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
      </PageShell>

      {/* Form Dialog — outside PageShell so it renders at the root level */}
      <ReusableFormDialog
        open={openFormDialog}
        onClose={() => {
          setOpenFormDialog(false);
          setCurrentItem(initialFormValues);
          setIsEditing(false);
        }}
        title={
          isEditing
            ? `Edit ${moduleName.slice(0, -1)}: ${
                getItemName ? getItemName(currentItem) : currentItem?.name || ""
              }`
            : `Add New ${moduleName.slice(0, -1)}`
        }
        initialValues={currentItem}
        validationSchema={validationSchema}
        fields={resolvedFormFields}
        onSubmit={handleSave}
        loading={btnLoading}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        onConfirm={confirmDelete}
        title={`Delete ${moduleName.slice(0, -1)}`}
        itemName={getItemName ? getItemName(itemToDelete) : itemToDelete?.name}
        itemType={moduleName.slice(0, -1)}
        warningText={
          deleteWarning ||
          `This action cannot be undone. All associated records will remain but will no longer be linked to this ${moduleName
            .slice(0, -1)
            .toLowerCase()}.`
        }
        loading={deleteLoading}
      />
    </>
  );
}

export default ModuleLayout;
